
import { useLanguage } from '../../../contexts/LanguageContext';
import { translations } from '../../../mocks/translations';

export default function MissionSection() {
  const { language } = useLanguage();
  const t = translations[language].mission;

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full mb-4">
          <i className="ri-lightbulb-line text-teal-700"></i>
          <span className="text-sm font-medium text-teal-800">{t.badge}</span>
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.title}</h2>
        <p className="text-lg text-gray-600">{t.subtitle}</p>
      </div>

      {/* Main Mission with Image */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-12">
          <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/efeb68112c70c7e18fe1b4585d7efd2f.jpg"
              alt="Global Energy Strategy"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-900/40 to-transparent"></div>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 rounded-2xl border border-teal-200">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl mb-6 shadow-lg">
              <i className="ri-global-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.mainTitle}</h3>
            <p className="text-base text-gray-700 leading-relaxed">
              {t.mainDescription}
            </p>
          </div>
        </div>

        {/* Three Cards with Images */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden">
            <div className="h-48 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Hands%20shaking%20in%20business%20partnership%20deal%20with%20oil%20refinery%20in%20background%2C%20professional%20corporate%20handshake%2C%20trust%20and%20reliability%20concept%2C%20warm%20golden%20lighting%2C%20clean%20professional%20style&width=400&height=250&seq=mission2&orientation=landscape"
                alt="Trust and Partnership"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mb-4">
                <i className="ri-hand-heart-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t.card1Title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t.card1Description}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden">
            <div className="h-48 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20at%20modern%20oil%20facility%2C%20engineer%20with%20safety%20helmet%20checking%20equipment%2C%20professional%20industrial%20safety%20standards%2C%20clean%20modern%20facility%2C%20teal%20and%20white%20color%20scheme&width=400&height=250&seq=mission3&orientation=landscape"
                alt="Quality Assurance"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg mb-4">
                <i className="ri-shield-check-line text-2xl text-emerald-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t.card2Title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t.card2Description}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden">
            <div className="h-48 relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Global%20shipping%20and%20logistics%20network%20with%20cargo%20ships%20at%20port%2C%20international%20trade%20and%20energy%20transportation%2C%20aerial%20view%20of%20modern%20port%20facility%2C%20blue%20ocean%20and%20clear%20sky%2C%20professional%20photography&width=400&height=250&seq=mission4&orientation=landscape"
                alt="Global Reach"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mb-4">
                <i className="ri-earth-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t.card3Title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t.card3Description}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Two Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-md">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-focus-3-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{t.strategicTitle}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t.strategicDescription}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-md">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                <i className="ri-line-chart-line text-2xl text-emerald-600"></i>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{t.sustainableTitle}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t.sustainableDescription}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
